import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { format } from 'd3-format';
import cuid from 'cuid';
import * as d3 from 'd3-selection';
import { extent as arrayExtent } from 'd3-array';
import { line as shapeLine } from 'd3-shape';
import { scaleOrdinal } from 'd3-scale';
import { timeMinute } from 'd3-time';

import widgetWrapper from './widgetWrapper';

import {
  chartAxis,
  chartAxisLabel,
  chartLegend,
  colorScheme,
  formatTimestamp,
  getCurveType,
  getScaleType,
} from './widgetUtils';

import './LineChartWidget.scss';

class LineChartWidget extends Component {

  static propTypes = {
    series: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.number.isRequired })),
      keys: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ])),
      xDomain: PropTypes.arrayOf(PropTypes.number),
      yDomain: PropTypes.arrayOf(PropTypes.number),
      yAxis: PropTypes.shape({
        label: PropTypes.string,
        type: PropTypes.string,
        tickArguments: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
        tickValues: PropTypes.arrayOf(PropTypes.number),
      }),
      margin: PropTypes.shape({
        top: PropTypes.number,
        right: PropTypes.number,
        left: PropTypes.number,
        bottom: PropTypes.number,
      }),
      points: PropTypes.shape({
        color: PropTypes.string,
        radius: PropTypes.number,
      }),
      colors: PropTypes.arrayOf(PropTypes.string),
    }),
    title: PropTypes.string,
    subtitle: PropTypes.string,
    timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
  };

  static defaultProps = {
    title: '',
    subtitle: '',
    timestamp: '',
    className: '',
    series: {},
  };

  constructor(...args) {
    super(...args);
    this.chartID = cuid();
    this.chartRef = React.createRef();
    this.legendRef = React.createRef();
  }

  componentDidMount() {
    window.addEventListener('resize', this.doD3Rendering);
    this.doD3Rendering();
  }

  componentDidUpdate(_prevProps, _prevState) {
    this.doD3Rendering();
  }

  doD3Rendering() {
    if (this.props.series) {
      const series = this.props.series;
      const {
        data: dataArray = [],
        keys = ['value'],
        margin: {
          top: marginTop = 10,
          right: marginHeight = 40,
          left: marginLeft = 40,
          bottom: marginBottom = 30,
        } = {},
        points: {
          color: pointColor = null,
          radius: pointRadius = 4,
        } = {},
        colors = [],
      } = series;

      const xAxisProps = Object.assign({
        label: '',
        type: 'time',
        grid: true,
        orient: 'bottom',
        labelPadding: 6,
        tickArguments: [],
        tickValues: null,
        tickFormat: null,
        tickSize: 6,
        tickSizeInner: 6,
        tickSizeOuter: 6,
        tickPadding: 3,
        tickIntervalMinutes: null, // non-d3 field
      }, series.xAxis);

      if (xAxisProps.tickIntervalMinutes) {
        xAxisProps.tickArguments = [timeMinute.every(xAxisProps.tickIntervalMinutes)];
      }

      const yAxisProps = Object.assign({
        label: '',
        type: 'linear',
        grid: true,
        orient: 'left',
        labelPadding: 6,
        tickArguments: [],
        tickValues: null,
        tickFormat: null,
        tickSize: 6,
        tickSizeInner: 6,
        tickSizeOuter: 6,
        tickPadding: 3,
      }, series.yAxis);

      if (typeof xAxisProps.tickFormat === 'string') {
        xAxisProps.tickFormat = format(xAxisProps.tickFormat);
      }
      if (typeof yAxisProps.tickFormat === 'string') {
        yAxisProps.tickFormat = format(yAxisProps.tickFormat);
      }

      // Persist the color scale.
      const scheme = colors instanceof Array && colors.length > 0 ? colors : colorScheme;

      const colorScale = this.colorScale
        || (this.colorScale = scaleOrdinal().range(scheme));

      const xAccessor = (d => d.key);
      const yAccessor = ((d, k) => d[k]);
      const colorAccessor = (k => colorScale(k));
      const getDatumKey = d => xAccessor(d.data);
      const getDatumValue = d => d[0];

      let svg = d3.select(this.chartRef.current)
        .data([0]);
      svg = svg
        .enter()
        .append('svg')
        .attr('class', 'line-chart')
        .merge(svg);

      const legend = d3.select(this.legendRef.current)
        .data([keys.length > 1 ? keys : []]);
      legend.enter()
        .append('ul')
        .attr('class', 'legend')
        .merge(legend)
        .call(chartLegend, colorAccessor);

      const width = svg.node().parentNode.clientWidth;
      const height = svg.node().parentNode.clientHeight;
      const chartWidth = width - marginLeft - marginHeight;
      const chartHeight = height - marginTop - marginBottom;

      svg
        .attr('width', width)
        .attr('height', height);

      // Set the clip path.
      let defs = svg
        .selectAll('defs')
        .data([0]);
      defs = defs
        .enter()
        .append('defs')
        .merge(defs);
      let clipPath = defs
        .selectAll('clipPath')
        .data([0]);
      clipPath = clipPath
        .enter()
        .append('clipPath')
        .merge(clipPath)
        .attr('id', `clip-linechart-${this.chartID}`);
      const clipRect = clipPath
        .selectAll('rect')
        .data([0]);
      clipRect
        .enter()
        .append('rect')
        .merge(clipRect)
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', chartWidth)
        .attr('height', chartHeight);

      if (chartWidth <= 0 || chartHeight <= 0) {
        console.log(`LineChartWidget: chartWidth is ${chartWidth} and chartHeight is ${chartHeight}, returning.`);
        return;
      }

      // Set the chart margin.
      let margin = svg.selectAll('.margin')
        .data([0]);
      margin = margin.enter()
        .append('g')
        .attr('class', 'margin')
        .merge(margin)
        .attr('transform', `translate(${marginLeft}, ${marginTop})`);

      // Group the data into lines, similar to the results of d3.stack(), but
      // without the stacking.
      let key;
      let group;
      let value;
      const grouped = keys.map((k) => {
        key = k instanceof Array ? k[0] : k;
        group = dataArray.map((d) => {
          value = [yAccessor(d, key)];
          value.data = d;
          return value;
        });
        group.key = key;
        return group;
      });

      // Examine the data for domains.
      let extent;
      const xDomain = series.xDomain
        || arrayExtent(dataArray, xAccessor);
      const yDomain = series.yDomain
        || grouped.reduce((e, s) => {
          extent = arrayExtent(s, getDatumValue);
          if (e[0] === undefined || extent[0] < e[0]) { e[0] = extent[0]; }
          if (e[1] === undefined || extent[1] > e[1]) { e[1] = extent[1]; }
          return e;
        }, []);

      const noData = (xDomain[0] === xDomain[1] || yDomain[0] === yDomain[1]);
      if (noData) {
        margin.append('g')
          .attr('transform', `translate(${chartWidth / 2},${chartHeight / 2})`)
          .append('text')
          .attr('class', 'no-data')
          .attr('text-anchor', 'center')
          .text('NO DATA');
      }

      // Create the scales.
      const xScale = (getScaleType(xAxisProps.type))()
        .domain(xDomain)
        .range([0, chartWidth]);
      const yScale = (getScaleType(yAxisProps.type))()
        .domain(yDomain)
        .range([chartHeight, 0])
        .nice();

      // Draw the axes.
      const axis = margin.selectAll('.axis')
        .data([xAxisProps, yAxisProps]);
      axis.enter()
        .append('g')
        .attr('class', (d, i) => `axis ${i ? 'y' : 'x'}`)
        .merge(axis)
        .call(chartAxis, xScale, yScale, !noData);

      // Draw each line series.
      const getX = (d, i) => xScale(getDatumKey(d, i));
      const getY = (d, i) => yScale(getDatumValue(d, i));
      const isPoint = d => d.data.mark;
      const generator = shapeLine()
        .x(getX)
        .y(getY)
        .defined(d => Number.isFinite(d[0]))
        .curve(getCurveType(series.curve));

      const line = margin.selectAll('.series')
        .data(grouped, d => d.key);
      line.exit().remove();
      line.enter()
        .append('path')
        .attr('clip-path', `url(#clip-linechart-${this.chartID})`)
        .attr('class', 'series')
        .merge(line)
        .attr('d', generator)
        .style('stroke', (d, i) => colorAccessor(d.key, i))
        .style('stroke-width', series.strokeWidth)
        .style('fill', 'none');

      // Draw the points.
      const points = grouped.reduce((f, s) => {
        s.forEach((d) => { if (isPoint(d)) { f.push(d); } });
        return f;
      }, []);
      const point = margin.selectAll('.point')
        .data(points);
      point.exit().remove();
      point.enter()
        .append('circle')
        .attr('class', 'point')
        .merge(point)
        .attr('cx', getX)
        .attr('cy', getY)
        .attr('r', pointRadius)
        .style('fill', pointColor);

      // Create the axis labels. They are layered above the chart content.
      const axisLabel = margin.selectAll('.label')
        .data([[xAxisProps, yAxisProps.orient], [yAxisProps, xAxisProps.orient]]);
      axisLabel.enter()
        .append('text')
        .attr('class', (d, i) => `label ${i ? 'y' : 'x'}`)
        .merge(axisLabel)
        .call(chartAxisLabel, xScale, yScale);
    }
  }

  render() {
    const {
      title = '',
      subtitle = '',
      timestamp = '',
      className = '',
    } = this.props;
    return (
      <div className={`widget-threshold-colors LineChartWidget ${className}`}>
        <div className="header">
          <div className="title">{title}</div>
          <div className="subtitle">{subtitle}</div>
        </div>
        <div className="content">
          <svg className="line-chart" ref={this.chartRef} />
        </div>
        <div className="footer">
          <ul className="legend" ref={this.legendRef} />
          <div className="timestamp">{formatTimestamp(timestamp)}</div>
        </div>
      </div>
    );
  }
}

export default widgetWrapper(LineChartWidget);
